import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { SalesApiService, SalesChannelRecord } from './sales-api.service';

@Component({
  standalone: true,
  selector: 'ain-sales-channels',
  imports: [AinPageComponent, AinStateComponent, FormsModule, TableModule, ButtonModule, InputTextModule],
  template: `
    <ain-page [title]="t('sales.channels.title')" [description]="t('sales.channels.description')">
      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else {
        <section class="panel">
          <div class="toolbar">
            <button pButton type="button" [label]="t('sales.action.create')" (click)="openCreate()"></button>
            <button pButton type="button" [label]="t('sales.action.edit')" severity="secondary" [disabled]="selectedRows().length !== 1" (click)="openSelected()"></button>
            <button pButton type="button" [label]="t('sales.action.refresh')" severity="secondary" (click)="load()"></button>
          </div>

          <div class="filter-toggle">
            <button pButton type="button" [label]="showFilters() ? t('sales.filter.hide') : t('sales.filter.show')" severity="secondary" (click)="showFilters.set(!showFilters())"></button>
          </div>

          @if (showFilters()) {
            <div class="filters">
              <label>
                <span>{{ t('sales.channels.fields.code') }}</span>
                <input pInputText type="text" [(ngModel)]="filters.code" (ngModelChange)="applyFilters()" />
              </label>
              <label>
                <span>{{ t('sales.channels.fields.name') }}</span>
                <input pInputText type="text" [(ngModel)]="filters.name" (ngModelChange)="applyFilters()" />
              </label>
            </div>
          }

          <p-table
            [value]="rows()"
            dataKey="id"
            [paginator]="true"
            [rows]="15"
            [selection]="selectedRows()"
            (selectionChange)="onSelectionChange($event)">
            <ng-template pTemplate="header">
              <tr>
                <th style="width: 44px"><p-tableHeaderCheckbox /></th>
                <th>{{ t('sales.channels.fields.code') }}</th>
                <th>{{ t('sales.channels.fields.name') }}</th>
                <th>{{ t('sales.channels.fields.channelType') }}</th>
                <th>{{ t('sales.channels.fields.currency') }}</th>
                <th>{{ t('columns.status') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td><p-tableCheckbox [value]="row" /></td>
                <td>{{ row.code }}</td>
                <td><button type="button" class="link-btn" (click)="openDetail(row.id)">{{ row.name }}</button></td>
                <td>{{ row.channelType }}</td>
                <td>{{ row.defaultCurrencyCode }}</td>
                <td>{{ row.isActive ? t('sales.channels.active') : t('sales.channels.inactive') }}</td>
              </tr>
            </ng-template>
          </p-table>

          @if (error()) {
            <p class="error">{{ error() }}</p>
          }
        </section>
      }
    </ain-page>
  `,
  styles: [`
    .panel { display: grid; gap: 10px; }
    .toolbar { display: flex; gap: 8px; flex-wrap: wrap; }
    .filter-toggle { display: flex; }
    .filters { border: 1px solid #e2e8f0; border-radius: 10px; padding: 10px; display: grid; gap: 10px; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); }
    .filters label { display: grid; gap: 6px; }
    .link-btn { border: 0; background: none; color: #1d4ed8; cursor: pointer; font-weight: 600; padding: 0; }
    .error { margin: 0; color: #b91c1c; }
  `]
})
export class SalesChannelsComponent implements OnInit {
  private readonly api = inject(SalesApiService);
  private readonly router = inject(Router);
  private readonly localization = inject(LocalizationService);

  readonly loading = signal(true);
  readonly error = signal('');
  readonly showFilters = signal(true);
  readonly sourceRows = signal<SalesChannelRecord[]>([]);
  readonly rows = signal<SalesChannelRecord[]>([]);
  readonly selectedRows = signal<SalesChannelRecord[]>([]);
  readonly filters = { code: '', name: '' };

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.error.set('');
    this.api.listChannels().subscribe({
      next: channels => {
        this.sourceRows.set(channels);
        this.applyFilters();
        this.loading.set(false);
      },
      error: () => {
        this.sourceRows.set([]);
        this.rows.set([]);
        this.error.set(this.t('sales.channels.errorLoad'));
        this.loading.set(false);
      }
    });
  }

  applyFilters(): void {
    const code = this.filters.code.trim().toLowerCase();
    const name = this.filters.name.trim().toLowerCase();
    this.rows.set(
      this.sourceRows().filter(row =>
        (!code || row.code.toLowerCase().includes(code))
        && (!name || row.name.toLowerCase().includes(name)))
    );
  }

  onSelectionChange(selection: SalesChannelRecord[] | SalesChannelRecord | null): void {
    this.selectedRows.set(Array.isArray(selection) ? selection : selection ? [selection] : []);
  }

  openCreate(): void {
    void this.router.navigate(['/sales/channels/new']);
  }

  openSelected(): void {
    const row = this.selectedRows()[0];
    if (row) {
      this.openDetail(row.id);
    }
  }

  openDetail(id: string): void {
    void this.router.navigate(['/sales/channels', id]);
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
